
const React = window.React;
const { useState, useEffect, useRef } = React;
const { createRoot } = window.ReactDOM.client;
import { decodeEntities } from '@wordpress/html-entities';

const defaultPostTypesToSearch = [
    { label: 'Articles', value: 'post' },
    { label: 'Pages', value: 'page' }
];

function initSearchPage(){

    const elSearchResults = document.getElementById('search-results');
    const postSlugsAndLabels = elSearchResults?.dataset?.searchSettingsPostTypes ? JSON.parse( elSearchResults.dataset.searchSettingsPostTypes ) : {};
    const root = createRoot(elSearchResults);

    let postTypesToSearch = Object.keys(postSlugsAndLabels)?.length > 0 ? Object.keys(postSlugsAndLabels).map( key => ( { label: postSlugsAndLabels[key].searchLabel, value: key } ) ) : defaultPostTypesToSearch;

    // Filter if enabled is false
    postTypesToSearch = postTypesToSearch.filter( pt => postSlugsAndLabels[pt.value]?.enabled === true );

    let defaultPage = 1;
    const urlParams = new URLSearchParams(window.location.search);
    
    // Default page from /page/num or query param ?page=num
    const pageParam = urlParams.get('page');
    const path = window.location.pathname;
    const pageMatchesFromPath = path.match(/\/page\/(\d+)/);
    if (pageParam) {
        defaultPage = parseInt(pageParam, 10);
    } else if (pageMatchesFromPath) {
        defaultPage = parseInt(pageMatchesFromPath[1], 10);
    }
    
    // Get ?s param value
    const searchParam = urlParams.get('s');
    
    root.render(<SearchApp query={searchParam || ''} page={defaultPage || 1} postTypesToSearch={postTypesToSearch}/>);

}

document.addEventListener("DOMContentLoaded", function() {
    initSearchPage();
});

const listItemClasses = "px-md-2 px-3 mx-n3 px-lg-3 mx-lg-n3 py-3 py-lg-3 rounded-4";
const ulClasses = "mx-0 my-4 my-lg-4 list-unstyled";

function SearchApp( { query: initialQuery, page: initialPage, postTypesToSearch } ) {

    const [query, setQuery] = useState(initialQuery);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(parseInt(initialPage, 10));
    const [totalResults, setTotalResults] = useState(0);
    const [postTypes, setPostTypes] = useState(postTypesToSearch);
    const [hasSearched, setHasSearched] = useState(initialQuery?.length >= 3);
    const [useFilters, setUseFilters] = useState(false);
    const [sortBy, setSortBy] = useState('recent'); // 'relevance' or 'recent'

    const hasAnime = typeof anime === 'function';

    const listRef = useRef(null);


    const getPostTypesString = ( postTypes ) => {
        return postTypes.reduce((acc, pt) => acc + pt.value + ',', '').slice(0, -1);
    }

    // Update query when input changes
    useEffect(() => {
        const elSearchInput = document.getElementById('search-input');

        function handleInput(event){
            setQuery(event.target.value?.trim?.());
            setCurrentPage(1);
            setTotalPages(0);
            setTotalResults(0);
        }

        elSearchInput.addEventListener('input', handleInput);

        return () => {
            elSearchInput.removeEventListener('input', handleInput);
        };
    }, []);

    useEffect(() => {
        if (query.length < 3) {
            setResults([]);
            return;
        }

        setHasSearched(true);
        setLoading(true);
        const abortController = new AbortController();
        
        const debounceTimeout = setTimeout(() => {
            const sortParam = sortBy === 'recent' ? '&sort_by=recent' : '';
            fetch(`/wp-json/adw/v1/search-adw?search=${encodeURIComponent(query)}&type=post&subtype=${getPostTypesString(postTypes)}&per_page=10&page=${currentPage}${sortParam}`, {
                signal: abortController.signal
            })
                .then(response => {
                    const totalPagesHeader = response.headers.get('X-WP-TotalPages');
                    if (totalPagesHeader) {
                        setTotalPages(parseInt(totalPagesHeader, 10));
                    }
                    const totalResultsHeader = response.headers.get('X-WP-Total');
                    if (totalResultsHeader) {
                        setTotalResults(parseInt(totalResultsHeader, 10));
                    }
                    return response.json();
                })
                .then(data => {
                    setResults(data);
                    setLoading(false);
                })
                .catch(error => {
                    if (error.name !== 'AbortError') {
                        console.error('Search error:', error);
                        setLoading(false);
                    }
                });
        }, 300);

        return () => {
            clearTimeout(debounceTimeout);
            abortController.abort();
        };
    }, [query, currentPage, postTypes, sortBy]);

    useEffect( () => {
        if( !useFilters ){
            setPostTypes(postTypesToSearch);
        }
    }, [useFilters] )

    useEffect( () => {
        if( hasAnime ){
            // Stagger results fade-in
            anime({
                targets: listRef.current?.children,
                opacity: [0, 1],
                translateY: [5, 0],
                delay: anime.stagger(30),
                duration: 500,
                easing: 'easeOutQuad'
            });
        }
    }, [results] );

    // Update URL without reloading the page
    useEffect(() => {
        const url = new URL(window.location);
        url.searchParams.set('s', query);
        if (currentPage > 1) {
            url.searchParams.set('page', currentPage);
        } else {
            url.searchParams.delete('page');
        }
        window.history.replaceState({}, '', url);
    }, [query, currentPage]);

    const Filters = () => {
        return (
            <>
                <div className="d-flex flex-row flex-wrap justify-content-center justify-content-lg-end gap-3 row-gap-2 mt-3" >
                    {postTypesToSearch.map(postType => (
                        <div key={postType.value} className="form-check ">
                            <input
                                className="form-check-input mb-0"
                                type="checkbox"
                                // If only one is checked, the checkbox is disabled
                                disabled={postTypes.length === 1 && postTypes.some(pt => pt.value === postType.value)}
                                id={`filter-${postType.value}`}
                                checked={postTypes.some(pt => pt.value === postType.value)}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setPostTypes([...postTypes, postType]);
                                    } else {
                                        // If last one is unchecked, check all
                                        if (postTypes.length === 1) {
                                            setPostTypes(postTypesToSearch);
                                        } else {
                                            setPostTypes(postTypes.filter(pt => pt.value !== postType.value));
                                        }
                                    }
                                    setCurrentPage(1);
                                }}
                            />
                            <label className="form-check-label" htmlFor={`filter-${postType.value}`}>
                                {postType.label}
                            </label>
                        </div>
                    ))}

                </div>
            </>
        )
    }

    const PostTypeBadge = ( { postType } ) => {

        let badgeLabel = '';
        if( postType?.includes( 'event' ) ) {
            badgeLabel = 'Event';
        } else if( postType?.includes( 'adw_podcast_show' ) ) {
            badgeLabel = 'Podcast';
        } else if( postType?.includes( 'adw_resource' ) ) {
            badgeLabel = 'Resource';
        } else if ( postType?.includes( 'webinar' ) ) {
            badgeLabel = 'Webinar';
        }

        return(
            badgeLabel ? <div className="d-none d-lg-block border border-primary text-primary rounded-2 fs-7 py-1 px-2 text-uppercase">{badgeLabel}</div> : null
        )
    }

    return (
        <>

        <div className="d-flex flex-column flex-lg-row row-gap-3 justify-content-between align-items-center mt-3 ">
            <p className="mb-0 fs-7 opacity-75">
            {
                totalResults && totalResults > 0 ?

                <>
                Found <span className="fw-bold">{totalResults.toLocaleString()}</span> results.
                </>
                :
                <>
                    { !loading && hasSearched && 'No results found. ' }
                    { query?.length < 3 && 'Enter at least three characters.' }
                    { <>&nbsp;</> }
                </>
            }
            </p>

            <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center gap-2">
                    <span className="fs-7">Sort By</span>
                    <select 
                        className="form-select form-select-sm" 
                        value={sortBy} 
                        onChange={(e) => {
                            setSortBy(e.target.value);
                            setCurrentPage(1);
                        }}
                        style={{width: 'auto', minWidth: '120px'}}
                    >
                        <option value="relevance">Relevance</option>
                        <option value="recent">Recent</option>
                    </select>
                </div>
                
                <div className="d-flex align-items-center gap-1 ">
                    <input id="search-filter-toggle" {...{checked: useFilters}} onChange={() => setUseFilters(!useFilters)} className={`d-none tgl tgl-light control control-length mixitup-control-${useFilters ? 'active' : 'inactive'}`} type="checkbox" />
                    <label className="tgl-btn mb-0" htmlFor="search-filter-toggle" style={{transform: 'scale( 0.8 )'}}></label>
                    <span className="fs-7">Filter</span>
                </div>
            </div>
        </div>
        {
            useFilters && <Filters />
        }
        {
            results?.length > 0 && !loading ? (
                <>
                    
                    {
                        loading && <PlaceholderPostList /> 
                    }
                    <ul ref={listRef} className={ulClasses}>
                        {results.map(result => (
                            <li key={result.id} className={`${listItemClasses} `} style={{opacity: hasAnime ? 0 : 1}}>
                                <a className=" text-decoration-none row d-flex align-items-center " href={result.url}>
                                    <div className="col-lg-3" style={{maxWidth: '90px'}}>
                                        {
                                            result?.thumbnail?.thumbnail ?
                                            <img src={result.thumbnail.thumbnail} alt={decodeEntities(result.thumbnail.alt)} className="w-100 rounded-3"
                                                style={{aspectRatio: '1/1', objectFit: 'cover', backgroundColor: '#bebebeff' }}    
                                            />
                                            :
                                            <img src="/wp-content/themes/adweek-next/src/images/logos/adweek-square-mark-black.svg" alt="Adweek" className="w-100 rounded-3"
                                                style={{aspectRatio: '1/1', objectFit: 'cover', backgroundColor: '#c1c1c1ff' }}
                                            />
                                        }
                                    </div>
                                    <div className="col d-flex ps-0 ps-md-3 align-items-center gap-4">
                                        
                                        <PostTypeBadge postType={result.subtype} />

                                        <div className="d-flex flex-column gap-2">

                                            <div className="d-inline-flex gap-3 justify-content-start align-items-baseline">

                                                <h3 className=" font-primary h5 mb-0">
                                                    {result?.is_exclusive && (
                                                        <span className="article-label font-pre-heading fs-8 text-uppercase rounded-end-top-3 rounded-start-bottom-3">
                                                            Exclusive
                                                        </span>
                                                    )}
                                                    <span dangerouslySetInnerHTML={{ __html: decodeEntities( result.title ) }} />
                                                </h3>
                                                
                                            </div>
                                            <div className="d-flex gap-3 align-items-baseline">
                                                <span className="fs-7 d-none d-lg-block ">
                                                    {result?.date_formatted?.length > 0 ? result.date_formatted : ''}
                                                </span>
                                            </div>
                                        </div>
                                        
                                    </div>
                                </a>
                                
                            </li>
                        ))}
                    </ul>
                    
                </>
            ) : (
                loading &&
                    <PlaceholderPostList />

            )}

            {
                results && results.length > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={page => setCurrentPage(page)}
                    />
                )
            }
        </>
    )
};

function Pagination({ currentPage, totalPages, onPageChange }) {
    if (totalPages <= 1) return null;

    const additionalPagesToShow = 0;

    // Calculate the range of pages to show (5 before and 5 after current page)
    const startPage = Math.max(1, currentPage - additionalPagesToShow);
    const endPage = Math.min(totalPages, currentPage + additionalPagesToShow);

    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
    }

    const pageClass = 'btn btn-sm btn-outline-primary';

    return (
        <div className="pagination d-flex align-items-stretch justify-content-center gap-2">
            <button
                disabled={currentPage === 1}
                onClick={() => onPageChange(currentPage - 1)}
                aria-label="Previous page"
                className="btn btn-sm btn-outline-primary"
            >
                <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor"><path d="M560-240 320-480l240-240 56 56-184 184 184 184-56 56Z"/></svg>
            </button>
            
            {pages.map(page => (
                <button
                    key={page}
                    // disabled={page === currentPage}
                    onClick={() => onPageChange(page)}
                    style={{transition: 'all 0.3s easeOut', minWidth: '5ch'}}
                    className={`${page === currentPage ? 'btn btn-primary btn-sm text-white ' : pageClass}`}
                >
                    {page}
                </button>
            ))}
            
            <button
                disabled={currentPage === totalPages}
                onClick={() => onPageChange(currentPage + 1)}
                aria-label="Next page"
                className="btn btn-sm btn-outline-primary"
            >
                <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor"><path d="M504-480 320-664l56-56 240 240-240 240-56-56 184-184Z"/></svg>
            </button>


        </div>
    );
}

function PlaceholderPostList() {
    const placeholders = Array.from({ length: 10 });

    return (
        <ul className={ulClasses} aria-hidden="true">
            {placeholders.map((_, index) => (
                <li key={index} className={listItemClasses}>
                    <div className="row d-flex align-items-center mb-0">
                        <div className="col-lg-3" style={{maxWidth: '90px'}}>
                            <div className="placeholder rounded-3" 
                                style={{width: '100%', aspectRatio: '1/1'}}
                            ></div>
                        </div>
                        <div className="col ps-0 ps-md-3 gap-3 d-flex flex-column">
                            <span className="placeholder col-10"></span>
                            <span className="placeholder col-3"></span>
                        </div>
                    </div>
                </li>
            ))}
        </ul>
    );
}